import React, { useState } from "react";
import Modal from "../../components/Modal";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import {
  DollarSign,
  CreditCard,
  Calendar,
  Building,
  FileText,
  Save,
  X,
} from "lucide-react";

const SupplierTransaction = ({ modalOpen, setModalOpen, supplier }) => {
  const { requestWithAuth } = useAuth();
  const [submittingForm, setSubmittingForm] = useState(false);
  const [paymentData, setPaymentData] = useState({
    supplier: supplier?.id,
    bank: supplier?.bank,
    cheque_number: supplier?.cheque_number,
  });

  const handleFormChange = (e) => {
    setPaymentData({
      ...paymentData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    Swal.fire({
      icon: "question",
      title: "Are you sure you want to proceed?",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSubmittingForm(true);
          const body = JSON.stringify(paymentData);

          const data = await requestWithAuth(
            `/supplier/transaction/`,
            "POST",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Payment Recorded Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something went wrong!");
        } finally {
          setSubmittingForm(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  return (
    <Modal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      title="Record Supplier Payment"
    >
      <form onSubmit={handlePayment} className="space-y-6">
        {/* Supplier Info Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Supplier Name
              </label>
              <p className="text-sm font-medium text-gray-900">
                {supplier?.name}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Amount Outstanding
              </label>
              <p className="text-sm font-bold text-red-600">
                GH₵{" "}
                {parseFloat(supplier?.total_outstanding || 0).toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building size={14} className="inline mr-1" />
              Bank *
            </label>
            <input
              type="text"
              name="bank"
              value={paymentData?.bank}
              onChange={handleFormChange}
              placeholder="Enter bank name"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CreditCard size={14} className="inline mr-1" />
              Cheque Number *
            </label>
            <input
              type="text"
              name="cheque_number"
              value={paymentData?.cheque_number}
              onChange={handleFormChange}
              placeholder="Enter cheque number"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign size={14} className="inline mr-1" />
              Amount Paid *
            </label>
            <input
              type="number"
              step="0.01"
              name="amount_paid"
              onChange={handleFormChange}
              placeholder="0.00"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar size={14} className="inline mr-1" />
              Transaction Date *
            </label>
            <input
              type="date"
              name="transaction_date"
              onChange={handleFormChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText size={14} className="inline mr-1" />
              Transaction Narration
            </label>
            <textarea
              name="narration"
              onChange={handleFormChange}
              placeholder="Enter transaction notes (optional)"
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="submit"
            disabled={submittingForm}
            className="px-6 py-2.5 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-2"
          >
            {submittingForm ? (
              <>
                <RotatingLines
                  strokeColor="white"
                  height="16"
                  width="16"
                  visible={true}
                />
                Processing...
              </>
            ) : (
              <>
                <Save size={16} />
                Record Payment
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SupplierTransaction;
